import React, { useEffect, useMemo } from 'react'
import {
  AnalysisSectionThemeProvider,
  SectionContentType,
  AnalysisDemographicsTab,
  AnalysisEmploymentTab,
  AnalysisTaxTab,
  AnalysisElectricityTab,
  AnalysisNoiseTab,
  AnalysisSunlightTab,
  AnalysisTransportAccessTab,
  AnalysisAmenitiesTab,
  AnalysisPurchaseMarketTab,
  AnalysisRentalMarketTab,
  AnalysisBuildingPermitsTab,
  AnalysisTransactionsTab,
  AnalysisListingsTab,
  AnalysisHistoryTab,
  AnalysisGeoAnalysisTab,
  AnalysisZoningTab,
  AnalysisLandSummaryDashboard,
  AnalysisBuildingDetailsTab,
  AnalysisRestrictionsTab,
  AnalysisFeasibilityStudyTab,
  SectionNotAvailable,
} from '@popety-io/ui'
import type { PublicLandAnalysis } from '@popety-io/types'
import {
  SharedFilesSection,
  SharedNotesSection,
} from '../../../components/SharedAttachments/SharedAttachments'
import { useMapSectionData } from '../contexts'

const sectionContent: Record<string, SectionContentType> = {
  summary: { title: 'Summary', icon: 'bar-chart', sectionColor: 'steel' },
  history: { title: 'History', icon: 'clock', sectionColor: 'steel' },
  geo: { title: 'Geo Analysis', icon: 'map', sectionColor: 'steel' },
  buildings: {
    title: 'Building Details',
    icon: 'building',
    sectionColor: 'steel',
  },
  zoning: { title: 'Zoning & Rules', icon: 'layers', sectionColor: 'bronze' },
  restrictions: {
    title: 'Restrictions',
    icon: 'alert-triangle',
    sectionColor: 'bronze',
  },
  construction_permits: {
    title: 'Building Permits',
    icon: 'building-permit',
    sectionColor: 'bronze',
  },
  feasibility: {
    title: 'Feasibility Study',
    icon: 'lightbulb',
    sectionColor: 'bronze',
  },
  purchase_market: {
    title: 'Purchase Market',
    icon: 'cash',
    sectionColor: 'forest',
  },
  rental_market: {
    title: 'Rental Market',
    icon: 'home',
    sectionColor: 'forest',
  },
  purchase_listings: {
    title: 'Purchase Listings',
    icon: 'listing-purchase',
    sectionColor: 'forest',
  },
  rental_listings: {
    title: 'Rental Listings',
    icon: 'listing-rental',
    sectionColor: 'forest',
  },
  transactions: {
    title: 'Transactions',
    icon: 'real-estate-transaction',
    sectionColor: 'forest',
  },
  transport: {
    title: 'Transport & Access',
    icon: 'car',
    sectionColor: 'deepBlue',
  },
  amenities: { title: 'Amenities', icon: 'store', sectionColor: 'deepBlue' },
  noise: {
    title: 'Noise Analysis',
    icon: 'activity',
    sectionColor: 'deepBlue',
  },
  sunlight: { title: 'Sunlight Study', icon: 'sun', sectionColor: 'deepBlue' },
  demographics: {
    title: 'Demographics',
    icon: 'users',
    sectionColor: 'wine',
  },
  employment: {
    title: 'Employment',
    icon: 'briefcase',
    sectionColor: 'wine',
  },
  taxes: { title: 'Taxes', icon: 'receipt', sectionColor: 'wine' },
  electricity: { title: 'Electricity', icon: 'zap', sectionColor: 'wine' },
  notes: { title: 'Notes', icon: 'edit', sectionColor: 'steel' },
  files: { title: 'Files', icon: 'file', sectionColor: 'steel' },
}

export interface PublicLandAnalysisContentProps {
  activeSection: string
  landData: PublicLandAnalysis
  hash: string
  isGrouping?: boolean
}

// Helper to check if a section is available in the shared data
const isSectionAvailable = (
  landData: PublicLandAnalysis,
  sectionId: string
): boolean => {
  if (
    !landData.available_sections ||
    landData.available_sections.length === 0
  ) {
    return true
  }
  return landData.available_sections.includes(sectionId)
}

export const PublicLandAnalysisContent: React.FC<
  PublicLandAnalysisContentProps
> = ({ activeSection, landData, hash, isGrouping = false }) => {
  const { setActiveSection, clearSectionData } = useMapSectionData()

  // Clear map section data when navigating to sections without dynamic layers
  useEffect(() => {
    const sectionsWithDynamicLayers = [
      'purchase_listings',
      'rental_listings',
      'transactions',
      'construction_permits',
      'amenities',
      'transport',
      'demographics',
      'employment',
      'purchase_market',
      'rental_market',
      'noise',
      'history',
      'sunlight',
      'zoning',
      'restrictions',
    ]

    if (sectionsWithDynamicLayers.includes(activeSection)) {
      // Bridge sidebar section to page-level context so the map picks it up
      setActiveSection(activeSection as any)
    } else {
      // Clear map layers for sections without dynamic layers
      setActiveSection(null)
      clearSectionData()
    }
  }, [activeSection])

  const section = sectionContent[activeSection]
  const sectionTheme = useMemo(
    () => ({ icon: section?.icon, sectionColor: section?.sectionColor }),
    [section?.icon, section?.sectionColor]
  )

  const renderContent = () => {
    if (!isSectionAvailable(landData, activeSection)) {
      return (
        <SectionNotAvailable sectionName={section?.title || activeSection} />
      )
    }

    switch (activeSection) {
      case 'summary':
        return <AnalysisLandSummaryDashboard />
      case 'history':
        return <AnalysisHistoryTab />
      case 'geo':
        return <AnalysisGeoAnalysisTab />
      case 'buildings':
        return <AnalysisBuildingDetailsTab />
      case 'zoning':
        return <AnalysisZoningTab />
      case 'restrictions':
        return <AnalysisRestrictionsTab />
      case 'construction_permits':
        return <AnalysisBuildingPermitsTab />
      case 'feasibility':
        return <AnalysisFeasibilityStudyTab />
      case 'purchase_market':
        return <AnalysisPurchaseMarketTab />
      case 'rental_market':
        return <AnalysisRentalMarketTab />
      case 'purchase_listings':
        return <AnalysisListingsTab type="purchase" />
      case 'rental_listings':
        return <AnalysisListingsTab type="rental" />
      case 'transactions':
        return <AnalysisTransactionsTab />
      case 'transport':
        return <AnalysisTransportAccessTab />
      case 'amenities':
        return <AnalysisAmenitiesTab />
      case 'noise':
        return <AnalysisNoiseTab />
      case 'sunlight':
        return <AnalysisSunlightTab />
      case 'demographics':
        return <AnalysisDemographicsTab />
      case 'employment':
        return <AnalysisEmploymentTab />
      case 'taxes':
        return <AnalysisTaxTab />
      case 'electricity':
        return <AnalysisElectricityTab />
      case 'notes':
        return <SharedNotesSection notes={landData.notes} />
      case 'files':
        return (
          <SharedFilesSection
            files={landData.files}
            hash={hash}
            kind={isGrouping ? 'grouping' : 'land'}
          />
        )
      default:
        return <AnalysisLandSummaryDashboard />
    }
  }

  return (
    <AnalysisSectionThemeProvider value={sectionTheme}>
      {renderContent()}
    </AnalysisSectionThemeProvider>
  )
}
